import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useForm } from '@/composables/useForm'
import type { Reason } from '@/types/Reason'
import type { Pagination } from '@/types'

interface ReasonFilters {
  id?: number
  context?: string
  reason_context_id?: number
}

type ReasonParams = {
  filter?: ReasonFilters
  sort?: string
  include?: string[]
}

export const useReasonStore = defineStore('reason', () => {
  // State
  const reasons = ref<Reason[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  const reasonsByContext = computed(() => {
    const grouped: Record<string, Reason[]> = {}
    reasons.value.forEach(reason => {
      const contextName = reason.reason_context?.name || 'Unknown'
      if (!grouped[contextName]) {
        grouped[contextName] = []
      }
      grouped[contextName].push(reason)
    })
    return grouped
  })

  // Actions
  const fetchReasons = async (params: ReasonParams = {}) => {
    loading.value = true
    error.value = null

    try {
      const form = useForm<ReasonParams, Pagination<Reason>>('get', 'reasons', params)

      const response = await form.submit()

      reasons.value = response.data?.data || response.data || []
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch reasons'
      console.error('Error fetching reasons:', err)
    } finally {
      loading.value = false
    }
  }

  const getReasonById = (id: number): Reason | undefined => {
    return reasons.value.find(reason => reason.id === id)
  }

  const getReasonsByContext = (contextName: string): Reason[] => {
    return reasons.value.filter(reason =>
      reason.reason_context?.name === contextName
    )
  }

  const getReasonsByContextId = (contextId: number): Reason[] => {
    return reasons.value.filter(reason =>
      reason.reason_context_id === contextId
    )
  }

  const getCancellationReasons = async () => {
    await fetchReasons({
      // TODO create reason enum
      filter: { reason_context_id: 1 },
      include: ['reasonContext'],
    })
    return reasons.value
  }

  const refreshReasons = async (params?: ReasonParams) => {
    await fetchReasons(params)
  }

  const clearError = () => {
    error.value = null
  }

  const reset = () => {
    reasons.value = []
    loading.value = false
    error.value = null
  }

  return {
    // State
    reasons,
    loading,
    error,

    reasonsByContext,

    // Actions
    fetchReasons,
    getReasonById,
    getReasonsByContext,
    getReasonsByContextId,
    getCancellationReasons,
    refreshReasons,
    clearError,
    reset,
  }
})
